import {
  createStyles,
  DirectionsBoatIcon,
  DirectionsRailwayIcon,
  ListItemIcon,
  LocalAirportIcon,
  MenuItem,
  PlaceIcon,
  SvgIconProps,
  Theme,
  WithStyles,
  withStyles,
} from '@freighthub/silhouette-core'
import { includes, intersection } from 'lodash-es'
import { ComponentType, createElement, FC } from 'react'

import { PlacesSearchType } from './PlaceService'

export const dataTestIds = {
  SUGGESTION_ITEM: 'suggestion-item',
}

interface IPlaceUIProps {
  icon: ComponentType<SvgIconProps>
  nameField: string
}

const placeTypePropertiesMap: Record<PlacesSearchType, IPlaceUIProps> = {
  rail: {
    icon: DirectionsRailwayIcon,
    nameField: 'name',
  },
  sea: {
    icon: DirectionsBoatIcon,
    nameField: 'name',
  },
  air: {
    icon: LocalAirportIcon,
    nameField: 'name',
  },
  address: {
    icon: PlaceIcon,
    nameField: 'description',
  },
}
type addressType = 'air' | 'sea' | 'rail' | 'address'

const jssSuggestionIcons = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '20px',
      width: '52px',
    },
    normal: {
      fontSize: theme.typography.h6.fontSize,
      width: '20px',
      height: '20px',
      top: '2px',
    },
    multiple: {
      fontSize: theme.typography.body1.fontSize,
      width: '16px',
      height: '16px',
      '&:nth-child(1)': {
        transform: 'translate(0, -5px)',
      },
      '&:nth-child(2)': {
        transform: 'translate(0, 8px)',
      },
    },
  })
type SuggestionIconsProps = {
  types: addressType[]
} & WithStyles<typeof jssSuggestionIcons>
const LeanSuggestionIcons: FC<SuggestionIconsProps> = (props) => {
  let { types } = props
  const isNotAddress = ['air', 'sea', 'rail'].reduce(
    (notAnAddress, curr) => notAnAddress || includes(types, curr),
    false
  )
  types = isNotAddress ? types : ['address']
  return (
    <span className={props.classes.root}>
      {types.map((type) =>
        createElement(placeTypePropertiesMap[type].icon, {
          key: type,
          className: types.length > 1 ? props.classes.multiple : props.classes.normal,
        })
      )}
    </span>
  )
}

const SuggestionIcons = withStyles(jssSuggestionIcons)(LeanSuggestionIcons)

const jssSuggestions = (theme: Theme) =>
  createStyles({
    item: {
      borderTop: `1px solid ${theme.palette.grey.A100}`,
      lineHeight: theme.typography.body1.fontSize,
      whiteSpace: 'normal',
      height: 'auto',
      minHeight: '48px',
      padding: '4px 4px 4px 0',
      '&:first-child': {
        borderTop: 'none',
      },
    },
  })
type TSuggestionsProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  suggestions: any[]
  focusIndex: number
  onSelect: (i: number) => void
  types: PlacesSearchType[]
} & WithStyles<typeof jssSuggestions>
const LeanPlaceAutocompleteSuggestions: FC<TSuggestionsProps> = (props) => {
  const { suggestions, onSelect, focusIndex, types, classes } = props
  return (
    <>
      {suggestions.map((place, i) => {
        const applicableTypes: PlacesSearchType[] = intersection(place.types, types)

        const placeType = applicableTypes.length > 0 ? place.types[0] : 'address'
        const { nameField } = placeTypePropertiesMap[placeType] || {}
        const name = place[nameField]

        if (!name) return null

        return (
          <MenuItem
            onClick={() => onSelect(i)}
            className={classes.item}
            selected={focusIndex === i}
            key={i + name}
            value={i}
            data-testid={dataTestIds.SUGGESTION_ITEM}
          >
            <ListItemIcon>
              <SuggestionIcons types={applicableTypes} />
            </ListItemIcon>
            <span>{name}</span>
          </MenuItem>
        )
      })}
    </>
  )
}

export const PlaceAutocompleteSuggestions = withStyles(jssSuggestions)(LeanPlaceAutocompleteSuggestions)
